import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  PlayArrow as PlayArrowIcon,
  Add as AddIcon
} from '@mui/icons-material';
import PageContainer from './common/PageContainer';

interface WordTemplate {
  id: string;
  name: string;
  type: string;
  filename: string;
  variables: Array<{
    name: string;
    type: string;
    label: string;
    required: boolean;
  }>;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

interface TemplatesResponse {
  success: boolean;
  templates: WordTemplate[];
  count: number;
  error?: string;
}

const WordTemplateList: React.FC = () => {
  const [templates, setTemplates] = useState<WordTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingTemplates, setDeletingTemplates] = useState<Set<string>>(new Set());
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/word-templates');
      const data: TemplatesResponse = await response.json();
      
      if (data.success) {
        setTemplates(data.templates);
      } else {
        throw new Error(data.error || '獲取模板列表失敗');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '獲取模板列表失敗';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId: string, templateName: string) => {
    const confirmDelete = window.confirm(`確定要刪除模板 "${templateName}" 嗎？此操作不可恢復。`);
    
    if (confirmDelete) {
      try {
        setDeletingTemplates(prev => new Set(prev).add(templateId));
        
        const response = await fetch(`/api/word-templates/${templateId}`, {
          method: 'DELETE'
        });
        
        const result = await response.json();
        
        if (result.success) {
          setTemplates(prev => prev.filter(t => t.id !== templateId));
        } else {
          throw new Error(result.error || '刪除模板失敗');
        }
        
      } catch (error) {
        console.error('刪除模板失敗:', error);
        alert(`刪除失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
      } finally {
        setDeletingTemplates(prev => {
          const newSet = new Set(prev);
          newSet.delete(templateId);
          return newSet;
        });
      }
    }
  };

  const handleDownloadTemplate = async (templateId: string, filename: string) => {
    try {
      const response = await fetch(`/api/word-templates/${templateId}/download`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error('下載失敗');
      }
    } catch (error) {
      console.error('下載模板失敗:', error);
      alert('下載失敗，請重試');
    }
  };

  const handleUseTemplate = (templateId: string) => {
    navigate(`/create-pdf?templateId=${templateId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN');
  };

  if (loading) {
    return (
      <PageContainer maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            加載模板列表中...
          </Typography>
        </Box>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert 
            severity="error" 
            action={
              <Button color="inherit" size="small" onClick={fetchTemplates}>
                重新加載
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth="lg">
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <Button 
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/create-template')}
          sx={{ borderRadius: 2 }}
        >
          創建新模板
        </Button>
      </Box>

      {templates.length === 0 ? (
        <Box sx={{ 
          textAlign: 'center', 
          py: 8,
          backgroundColor: 'background.paper',
          borderRadius: 2,
          border: '2px dashed',
          borderColor: 'divider'
        }}>
          <DescriptionIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            還沒有Word模板
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            創建你的第一個Word模板來開始生成PDF文檔
          </Typography>
          <Button 
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/create-template')}
          >
            創建模板
          </Button>
        </Box>
      ) : (
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 3 
        }}>
          {templates.map((template) => (
            <Card 
              key={template.id}
              elevation={2}
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 4,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <DescriptionIcon color="primary" sx={{ mr: 1, mt: 0.5 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="div" gutterBottom>
                      {template.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {template.filename}
                    </Typography>
                  </Box>
                </Box>

                {template.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {template.description}
                  </Typography>
                )}
                
                <Box sx={{ mb: 2 }}>
                  <Chip 
                    label={`${template.variables.length} 個變量`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mr: 1, borderRadius: 2 }}
                  />
                </Box>

                {template.variables.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      變量預覽:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {template.variables.slice(0, 3).map((variable) => (
                        <Chip
                          key={variable.name}
                          label={`{{${variable.name}}}`}
                          size="small"
                          sx={{ fontFamily: 'monospace', fontSize: '0.7rem', borderRadius: 2 }}
                        />
                      ))}
                      {template.variables.length > 3 && (
                        <Chip
                          label={`+${template.variables.length - 3} 更多`}
                          size="small"
                          color="info"
                          sx={{ borderRadius: 2 }}
                        />
                      )}
                    </Box>
                  </Box>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  創建時間: {formatDate(template.createdAt)}
                </Typography>
              </CardContent>

              <CardActions sx={{ p: 2, pt: 0, flexWrap: 'wrap', gap: 1 }}>
                <Tooltip title="使用模板">
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<PlayArrowIcon />}
                    onClick={() => handleUseTemplate(template.id)}
                    fullWidth
                    sx={{ borderRadius: 2 }}
                  >
                    使用模板
                  </Button>
                </Tooltip>
                
                <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                  <Tooltip title="下載模板">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownloadTemplate(template.id, template.filename)}
                      sx={{ flex: 1, borderRadius: 2 }}
                    >
                      下載
                    </Button>
                  </Tooltip>
                  
                  <Tooltip title="刪除模板">
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={deletingTemplates.has(template.id) ? <CircularProgress size={16} /> : <DeleteIcon />}
                      onClick={() => handleDeleteTemplate(template.id, template.name)}
                      disabled={deletingTemplates.has(template.id)}
                      sx={{ flex: 1, borderRadius: 2 }}
                    >
                      {deletingTemplates.has(template.id) ? '刪除中' : '刪除'}
                    </Button>
                  </Tooltip>
                </Box>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}
    </PageContainer>
  );
};

export default WordTemplateList;
